import React, { useMemo } from "react";
import { format, parseISO, isValid, startOfDay } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * Robuuste ISO-yyyy-MM-dd datumkiezer op basis van native
 * ``<input type="date">``. Werkt overal: desktop Chrome/Edge/Safari,
 * iPad/iOS, Android — én ook in shadcn/Radix Dialog modals waar de
 * vorige Radix-Popover-based implementatie een ``pointer-events: none``
 * conflict had op het body-element (P0 bugfix 2 Mrt 2026 — Reminders).
 *
 * Native input ondersteunt:
 *  - kalender-popout (vanuit het browser-OS)
 *  - handmatige invoer via toetsenbord (Tab/PijlOmhoog/Type cijfers)
 *  - ``min`` / ``max`` voor disabled-ranges
 *  - i18n via OS-locale (gebruiker ziet zijn eigen formaat)
 *  - accessibility (screen-reader native ondersteuning)
 *
 * Props:
 *  - value: string ("yyyy-MM-dd") of ""
 *  - onChange: (newIso: string) => void
 *  - testId: optional data-testid voor het input-element
 *  - placeholder: string (alleen voor aria-label / hover)
 *  - minDate: optional Date of ISO-string (yyyy-MM-dd)
 *  - maxDate: optional Date of ISO-string (yyyy-MM-dd)
 *  - disabled: optional boolean
 */
export default function DatePickerField({
  value,
  onChange,
  testId,
  placeholder = "Kies datum",
  minDate,
  maxDate,
  disabled = false,
}) {
  const minIso = useMemo(() => toIso(minDate), [minDate]);
  const maxIso = useMemo(() => toIso(maxDate), [maxDate]);

  return (
    <div className="relative w-full min-w-0 mt-1">
      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none z-10" />
      <input
        type="date"
        value={value || ""}
        min={minIso}
        max={maxIso}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || "")}
        data-testid={testId}
        aria-label={placeholder}
        placeholder={placeholder}
        className={cn(
          "w-full min-w-0 box-border rounded-none border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm",
          "focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]",
          disabled && "bg-gray-50 text-gray-400 cursor-not-allowed",
          !value && !disabled && "text-gray-500",
        )}
        autoComplete="off"
      />
    </div>
  );
}

function toIso(input) {
  if (!input) return undefined;
  let d = null;
  if (input instanceof Date) {
    d = isValid(input) ? input : null;
  } else if (typeof input === "string") {
    const parsed = parseISO(input);
    d = isValid(parsed) ? parsed : null;
  }
  if (!d) return undefined;
  return format(startOfDay(d), "yyyy-MM-dd");
}
